// Diet checks shared by every screen. Two kinds of knowledge:
//  • Ingredients are certain: an ingredient's `notDiet` is a fact (pork is not halal). A recipe
//    with such an ingredient breaks that diet: red mark, and "hide" removes it. No confirmation
//    can override that.
//  • Everything about the dish is only a warning: combination rules (`notDiet` on the recipe,
//    e.g. kosher for meat with milk) and ingredients that never declared their diets. Orange
//    mark, never hidden. A person decides by confirming the recipe fits.
// A recipe fits a diet only once a person confirmed it. (Later, AI checks add more warnings.)
import { DIETS, type Diet, type Id, type Ingredient, type Recipe } from '../types';

/** Every ingredient a recipe uses, including ingredients only linked from steps. */
export function recipeIngredientIds(r: Recipe): Id[] {
  return [...new Set([...r.ingredients, ...r.steps.flatMap(s => s.ingredients)].map(l => l.ingredientId))];
}

/** Built-in ingredients always declare their diets; the user's own must be confirmed once. */
export const dietsDeclared = (i: Ingredient): boolean => i.builtIn || i.dietsChecked === true;

const ORDER = new Map(DIETS.map((d, i) => [d, i]));
const sorted = (diets: Iterable<Diet>): Diet[] => [...new Set(diets)].sort((a, b) => (ORDER.get(a) ?? 0) - (ORDER.get(b) ?? 0));

/** All diets a recipe breaks (ingredients + the recipe's own declaration), in DIETS order. */
export function recipeBreaks(r: Recipe, all: Record<Id, Ingredient>): Diet[] {
  return sorted([...recipeIngredientIds(r).flatMap(id => all[id]?.notDiet ?? []), ...(r.notDiet ?? [])]);
}

/** Which of `diets` the given list of broken diets hits. */
export function dietConflicts(breaks: Diet[], diets: Diet[]): Diet[] {
  const wanted = new Set(diets);
  return breaks.filter(d => wanted.has(d));
}

/** Why a diet is flagged: an ingredient breaks it (certain), the dish declares it, or an ingredient declares nothing yet (warnings). */
export type DietReason = { kind: 'ingredient'; id: Id } | { kind: 'dish' } | { kind: 'undeclared'; id: Id };
export type DietStatus = {
  diet: Diet;
  /**
   * breaks: an ingredient breaks it (certain). issue: a warning nobody confirmed.
   * confirmed: a person said it fits. unreviewed: nothing flagged, nobody confirmed.
   */
  state: 'breaks' | 'issue' | 'confirmed' | 'unreviewed';
  /** What the check noticed (kept for confirmed diets too, for tracing). */
  reasons: DietReason[];
};
/** What lists show: red for certain breaks, orange for unconfirmed warnings. */
export type DietFlags = { breaks: Diet[]; unsure: Diet[] };
export const NO_FLAGS: DietFlags = { breaks: [], unsure: [] };

/** The recipe against each of the user's diets, in DIETS order. */
export function dietReview(r: Recipe, all: Record<Id, Ingredient>, diets: Diet[], confirmed: Diet[] = []): DietStatus[] {
  const ids = recipeIngredientIds(r);
  const undeclared = ids.filter(id => all[id] && !dietsDeclared(all[id]));
  return sorted(diets).map(diet => {
    const reasons: DietReason[] = [
      ...ids.filter(id => all[id]?.notDiet.includes(diet)).map(id => ({ kind: 'ingredient' as const, id })),
      ...(r.notDiet?.includes(diet) ? [{ kind: 'dish' as const }] : []),
      ...undeclared.map(id => ({ kind: 'undeclared' as const, id })),
    ];
    const state = reasons.some(x => x.kind === 'ingredient') ? 'breaks' : confirmed.includes(diet) ? 'confirmed' : reasons.length ? 'issue' : 'unreviewed';
    return { diet, state, reasons };
  });
}

/** Certain breaks (red, hidden by "hide") and unconfirmed warnings (orange, never hidden). */
export function dietFlags(review: DietStatus[]): DietFlags {
  return { breaks: review.filter(s => s.state === 'breaks').map(s => s.diet), unsure: review.filter(s => s.state === 'issue').map(s => s.diet) };
}

/** Symbol shown in the diet marks: a lucide icon key or a text glyph (religious diets). */
export type DietSymbol = { icon: 'vegan' | 'sprout' | 'wheatOff' | 'milkOff' | 'bone' | 'candyOff' | 'sun' | 'fish' | 'egg' | 'flower' | 'flame' | 'church' | 'dropletOff' } | { glyph: string };

// U+FE0E asks for the text (not emoji) form, so the glyph takes the mark's red colour.
const text = (g: string): DietSymbol => ({ glyph: `${g}︎` });

export const DIET_SYMBOLS: Record<Diet, DietSymbol> = {
  vegan: { icon: 'vegan' },
  vegetarian: { icon: 'sprout' },
  glutenFree: { icon: 'wheatOff' },
  dairyFree: { icon: 'milkOff' },
  paleo: { icon: 'bone' },
  keto: { icon: 'candyOff' },
  mediterranean: { icon: 'sun' },
  sattvic: { icon: 'flower' },
  rajasic: { icon: 'flame' },
  halal: text('☪'),
  kosher: text('✡'),
  pescatarian: { icon: 'fish' },
  ovoVegetarian: { icon: 'egg' },
  mormon: { icon: 'church' },
  buddhist: text('☸'),
  hindu: { glyph: 'ॐ' },
  shinto: text('⛩'),
  taoist: text('☯'),
  lenten: text('☦'),
  christian: text('✝'),
  jehovah: { icon: 'dropletOff' },
};
